import { ImageName } from "@mcleod/images";
import { Alignment, DisplayType, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutEdiStatusGeneralLayout } from "./autogen/AutogenLayoutEdiStatusGeneralLayout";
import { ClickEvent, Component, DataDisplayEvent, DataSourceExecutionEvent, Label, Layout } from "@mcleod/components";
import { EdiProfileStatusQuickInfo } from "./EdiProfileStatusQuickInfo";
import { TransmissionStatusQuickInfo } from "./TransmissionStatusQuickInfo";
import { CommonTooltips } from "@mcleod/common";
import { EdiStatusCityQuickInfo } from "./EdiStatusCityQuickInfo";

export class EdiStatusGeneralLayout extends AutogenLayoutEdiStatusGeneralLayout {
    override onLoad() {
        this._labelTransStatusOnDataDisplay();
        this._labelStatusOnDataDisplay();
        this.setupPartnerNameTooltipContent();
    }

  /** This is an event handler for the afterExecution event of sourceEdistatus. */
  sourceEdistatusAfterExecution(event: DataSourceExecutionEvent) {
    const row = this.mainDataSource.activeRow;
    if (row != null) {
      this.initialize(row);
    }
  }

  private _toggleIntercompanyLabels(intercompany: boolean) {
    this.labelIntercompanyYes.visible = intercompany
    this.labelIntercompany.visible = intercompany;
  }

  private _showFieldsByDirection(row: ModelRow<any>) {
    const customerId = row?.get("customer_id");
    const direction = row?.get("direction");
    const error = row?.get("error");
    const carrierId = row?.get("carrier_id");
    this.labelStatusPicture.text = null;
    if ("O" == direction) {
      if (customerId != null)
        this.sourceCustomer.search({ "id": customerId });
      this.labelStatusPicture.imageName = "0" == error ? ImageName.outboundShipStatus : ImageName.outboundShipmentStatusError;
      this.labelStatusPicture.tooltip = "0" == error ? "Outbound Shipment Status" : "Outbound Status has errors";
      this.labelCarrierLabel.visible = false;
      this.labelCarrier.visible = false;
      this.textboxReadyToSend.visible = false;
    } else {
      this.labelStatusPicture.imageName = "0" == error ? ImageName.inboundShipStatus : ImageName.inboundShipmentStatusError;
      this.labelStatusPicture.tooltip = "0" == error ? "Inbound Shipment Status" : "Inbound Status has errors";
      this.labelCustomerId.visible = false;
      this.labelBillTo.visible = false;
      this.citystateCustomer.visible = false;
      this.textboxTransmitted.visible = false;
      this.citystateStatusCity.caption = "Location";
      if (carrierId != null) {
        this.sourceCarrierPayee.search({ "id": carrierId });
      }
    }
    this.labelStatusPicture.tooltipPosition = Alignment.LEFT;
  }

  async labelCustomerIdOnDataDisplay(){
    if(this.activeRow != null){
        this.labelCustomerId.quickInfoOverrideSearchMethod = async (layout, keyValue) => {
            const partnerValue = this.mainDataSource.activeRow?.get("bill_plant");
            await layout.mainDataSource.search({ id: keyValue });
            const row = layout.mainDataSource.activeRow;
            if (row != null) {
                row.set("partner_value_visible", true);
                if (partnerValue != undefined) {
                    row.set("partner_value", partnerValue);
                }
            }
        };
    }
  }

    setupPartnerNameTooltipContent() {
        this.textboxPartner.tooltipDynamicContent = async () => {
            const row = this.sourceEdistatus.activeRow;
            const partnerID = row?.get("partner_id");
            const version = row?.get("version");
            const direction = row?.get("direction");
            const batch = row?.get("batch");
            if (partnerID != null && version != null && direction != null) {
                const layout = await Layout.getLoadedLayout("lme/datafusion/EdiProfileStatusQuickInfo") as EdiProfileStatusQuickInfo;
                await layout.sourceEdistatusProfile?.search({ partner_id: partnerID, version: version, direction: direction, batch: batch ?? 0 });
                return layout;
            }
            return null;
        }
    }

  sourceCarrierPayeeAfterExecution(event: DataSourceExecutionEvent) {
    this.sourceCarriersForAssignment.search({ "id": this.mainDataSource.activeRow?.get("carrier_id") });
  }

  sourceCarriersForAssignmentAfterExecution(event: DataSourceExecutionEvent) {
    this._addCarrierToolTip();
  }

  private _addCarrierToolTip() {
    const carrierId = this.mainDataSource.activeRow?.get("carrier_id");
    const carrierAssignmentRow = this.sourceCarriersForAssignment.activeRow;
    const name = this.sourceCarrierPayee.activeRow?.get("name");
    let tooltipData = null;

    tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
      id: carrierId,
      name: name,
      phone_number: carrierAssignmentRow?.get("phone_number"),
      icc_number: carrierAssignmentRow?.get("icc_number"),
      dot_number: carrierAssignmentRow?.get("dot_number")
    })
    CommonTooltips.setTooltipContentLayout(this.labelCarrier, tooltipData, "lme/powerbroker/CarrierQuickInfo", { minWidth: 100, minHeight: 100 });
    this.labelCarrier.tooltipDynamicOptions = { position: Alignment.RIGHT };
  }

  /** This is an event handler for the onDataDisplay event of labelErrorCode. */
  private _labelErrorCodeOnDataDisplay(row: ModelRow) {
    const errorDescription = row?.get("error_description");
    if ("No Error" == errorDescription) {
      this.textboxErrorCode.color = "success";
    } else {
      this.textboxErrorCode.color = "error";
      const id = row?.get("id");
      CommonTooltips.setTooltipContentLayout(this.textboxErrorCode, null, "lme/datafusion/StatusErrorQuickInfo", null, { "parent_id": id, "parent_tablename": "edistatus" });
      this.textboxErrorCode.tooltipDynamicOptions = { position: Alignment.BOTTOM };
    }
  }

    // Couldn't find where this was called...
    labelTransStatusOnDataDisplay() {
        this.textboxTransmitted.tooltipDynamicContent = async () => {
            if (this.mainDataSource?.activeRow?.get("batch") > "0") {
                const layout = await Layout.getLoadedLayout("lme/datafusion/TransmissionStatusQuickInfo") as TransmissionStatusQuickInfo;
                await layout.sourceEdiLog?.search({
                    direction: "O",
                    batch: this.mainDataSource.activeRow.get("batch"),
                    func_receiver_id: this.mainDataSource.activeRow.get("partner_id"),
                    receiver_id: this.mainDataSource.activeRow.get("alt_partner_id"),
                    func_version: this.mainDataSource.activeRow.get("version"),
                    transaction_type: 'S'
                });
                return layout;
            }
            return null;
        };
    }

  private _labelTransStatusOnDataDisplay() {
    this.generateTransmissionLogQuickInfo(this.textboxTransmitted, false);
  }

  private _labelStatusOnDataDisplay() {
    this.generateTransmissionLogQuickInfo(this.textboxReadyToSend, true);
  }

  private _setupCityQuickInfo(row: ModelRow<any>) {
    this.generateCityQuickInfo("ship");
    this.generateCityQuickInfo("cons");
    this.citystateStatusCity.addDataDisplayListener(() => { EdiStatusCityQuickInfo.makeStatusCityCallBack(this.citystateStatusCity, row); });
  }

  private generateCityQuickInfo(input: string) {
    const component = input === "ship" ? this.citystateFrom : this.citystateTo;
    const locationId = StringUtil.isEmptyString(this.mainDataSource.activeRow?.get(`${input}_loc_id`)) ? "--" : this.mainDataSource.activeRow?.get(`${input}_loc_id`);
    const partnerValue = input === "ship" ? this.mainDataSource.activeRow?.get("shipper_plant") : this.mainDataSource.activeRow?.get("consignee_plant");
    CommonTooltips.setTooltipContentTextboxes(component, () => [
      { caption: "Name", displayType: DisplayType.STRING, text: this.mainDataSource.activeRow?.get(`${input}_loc_name_inbound`) },
      { caption: "Location ID", displayType: DisplayType.STRING, text: locationId },
      { caption: "Address", displayType: DisplayType.STRING, text: this.mainDataSource.activeRow?.get(`${input}_address_inbound`) },
      { caption: "City", displayType: DisplayType.STRING, text: this.mainDataSource.activeRow?.get(`${input}_city_name_inbound`) },
      { caption: "State", displayType: DisplayType.STRING, text: this.mainDataSource.activeRow?.get(`${input}_state_inbound`) },
      { caption: "Zip Code", displayType: DisplayType.STRING, text: this.mainDataSource.activeRow?.get(`${input}_zip_code_inbound`) },
      { caption: "Partner Value", displayType: DisplayType.STRING, text: partnerValue }
    ]);
  }

    private generateTransmissionLogQuickInfo(component: Component, isInbound: boolean) {
        component.tooltipDynamicContent = async () => {
            if (this.mainDataSource?.activeRow?.get("batch") > "0") {
                const layout = await Layout.getLoadedLayout("lme/datafusion/TransmissionStatusQuickInfo") as TransmissionStatusQuickInfo;
                if (layout.sourceEdiLog !== null) {
                    const searchParameters = isInbound ? {
                    direction: "I",
                    batch: this.mainDataSource.activeRow.get("batch"),
                    func_sender_id: this.mainDataSource.activeRow.get("partner_id"),
                    sender_id: this.mainDataSource.activeRow.get("alt_partner_id"),
                    func_version: this.mainDataSource.activeRow.get("version"),
                    transaction_type: 'S'
                    } :
                    {
                        direction: "O",
                        batch: this.mainDataSource.activeRow.get("batch"),
                        func_receiver_id: this.mainDataSource.activeRow.get("partner_id"),
                        receiver_id: this.mainDataSource.activeRow.get("alt_partner_id"),
                        func_version: this.mainDataSource.activeRow.get("version"),
                        transaction_type: 'S'
                    }
                    await layout.sourceEdiLog.search(searchParameters);
                }
                return layout;
            }
            return null;
        };
    }

    editShipmentId(event: ClickEvent) {
        const shipId = this.textboxShipmentId;
        shipId.printable = false;
        shipId.focus();
        shipId.selectText();
    }

    textboxShipmentIdOnBlur(event: DataDisplayEvent) {
        this.textboxShipmentId.printable = true;

    }

    initialize(row: ModelRow) {
      if (row == null) {
        console.warn("initialize called with null row, skipping");
        return;
      }
      this._showFieldsByDirection(row);
      this._toggleIntercompanyLabels("Y" == row?.get("intercompany"));
      this._labelErrorCodeOnDataDisplay(row);
      this._setupCityQuickInfo(row);
      if (row.get("is_resend") != "Y") {
        this.imageIsResend.visible = false;
      }
    }
}
